import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  FlightTakeoff as FlightIcon,
  Route as RouteIcon,
  ArrowRightAlt as ArrowIcon,
  Info as InfoIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const VueloForm = () => {
  const { awb } = useParams(); // Si existe, es modo edición
  const isEditMode = Boolean(awb);
  const navigate = useNavigate();
  const { userBase } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vueloOriginal, setVueloOriginal] = useState(null);

  // Modo de creación: 'ruta' o 'manual'
  const [modo, setModo] = useState('ruta');

  // Datos para selects
  const [equipos, setEquipos] = useState([]);
  const [estaciones, setEstaciones] = useState([]);
  const [rutas, setRutas] = useState([]);
  const [clientes, setClientes] = useState([]);

  // Form data - origen automático basado en userBase
  // El número de vuelo (flight_number) se genera automáticamente en el backend
  const [formData, setFormData] = useState({
    master: '',
    ruta_id: '',
    origen: userBase || 'MIA',
    destino: '',
    destino_id: '',
    handler: '',
    equipo_id: '',
    fecha: new Date().toISOString().split('T')[0],
    cliente_id: '',
    cliente: '',
  });

  // Ruta seleccionada para preview
  const [selectedRuta, setSelectedRuta] = useState(null);

  useEffect(() => {
    fetchData();
  }, [awb]);

  // Actualizar origen cuando cambie userBase (solo en modo crear)
  useEffect(() => {
    if (userBase && modo === 'manual' && !isEditMode) {
      setFormData((prev) => ({ ...prev, origen: userBase }));
    }
  }, [userBase, modo, isEditMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [equiposRes, estacionesRes, rutasRes, clientesRes] = await Promise.all([
        api.get(endpoints.equipos),
        api.get(endpoints.guias.estaciones),
        api.get(endpoints.rutas.list),
        api.get(endpoints.oficina.clientesfact),
      ]);
      setEquipos(equiposRes.data.data || []);
      setEstaciones(estacionesRes.data.data || []);
      setRutas(rutasRes.data.data?.rutas || []);
      setClientes(clientesRes.data.data || []);

      // Si es modo edición, cargar datos del vuelo
      if (isEditMode && awb) {
        const vueloRes = await api.get(`${endpoints.vuelos.detalle}?awb=${awb}`);
        const vuelo = vueloRes.data.data.vuelo;
        const estadisticas = vueloRes.data.data.estadisticas;
        
        // Verificar si tiene carga - no se puede editar
        const tieneCarga = (estadisticas?.total_pallets || 0) > 0;
        if (tieneCarga) {
          setError('Este vuelo tiene carga asignada y no puede ser editado.');
          setLoading(false);
          return;
        }
        
        setVueloOriginal(vuelo);
        
        // Poblar el formulario
        setFormData({
          master: vuelo.master || '',
          ruta_id: vuelo.ruta_id || '',
          origen: vuelo.origen || userBase || 'MIA',
          destino: vuelo.Destino || vuelo.destino || '',
          destino_id: '',
          handler: vuelo.handler || '',
          equipo_id: vuelo.equipo_id || '',
          fecha: vuelo.fecha ? vuelo.fecha.split('T')[0] : '',
          cliente_id: vuelo.cliente_id || '',
          cliente: vuelo.cliente || '',
        });
        
        // Determinar modo basado en si tiene ruta_id
        if (vuelo.ruta_id) {
          setModo('ruta');
          const ruta = rutasRes.data.data?.rutas?.find(r => r.id == vuelo.ruta_id);
          setSelectedRuta(ruta || null);
        } else {
          setModo('manual');
        }
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar datos iniciales');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Si cambia ruta_id, actualizar preview
    if (name === 'ruta_id') {
      const ruta = rutas.find((r) => r.id == value);
      setSelectedRuta(ruta || null);
    }

    // Si cambia destino_id (modo manual), actualizar el código de destino
    if (name === 'destino_id') {
      const estacion = estaciones.find((e) => e.id == value);
      if (estacion) {
        setFormData((prev) => ({ ...prev, destino: estacion.codigo || estacion.code }));
      }
    }
  };

  const handleModoChange = (e, newModo) => {
    if (newModo) {
      setModo(newModo);
      // Limpiar campos según el modo
      if (newModo === 'ruta') {
        setFormData((prev) => ({ ...prev, destino: '', destino_id: '' }));
      } else {
        setFormData((prev) => ({ ...prev, ruta_id: '', origen: userBase || 'MIA' }));
        setSelectedRuta(null);
      }
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validaciones - en modo edición el destino ya está definido
    if (!isEditMode) {
      if (modo === 'ruta' && !formData.ruta_id) {
        setError('Seleccione una ruta');
        return;
      }
      if (modo === 'manual' && !formData.destino_id) {
        setError('Seleccione un destino');
        return;
      }
    }
    if (!formData.equipo_id) {
      setError('Seleccione un equipo');
      return;
    }
    if (!formData.fecha) {
      setError('La fecha es requerida');
      return;
    }

    try {
      setSaving(true);

      if (isEditMode) {
        // Modo edición - actualizar vuelo existente
        const payload = {
          awb: awb,
          master: formData.master,
          equipo_id: parseInt(formData.equipo_id),
          fecha: formData.fecha,
          handler: formData.handler,
        };

        // Cliente
        if (formData.cliente_id) {
          payload.cliente_id = parseInt(formData.cliente_id);
        }

        await api.put(endpoints.vuelos.update, payload);

        setSuccess('Vuelo actualizado exitosamente');
        setTimeout(() => {
          navigate('/vuelos');
        }, 1000);
      } else {
        // Modo creación
        const payload = {
          master: formData.master,
          equipo_id: parseInt(formData.equipo_id),
          fecha: formData.fecha,
          handler: formData.handler,
        };

        if (modo === 'ruta') {
          payload.ruta_id = parseInt(formData.ruta_id);
        } else {
          payload.origen = formData.origen || userBase;
          payload.destino = formData.destino;
          payload.escalado = 0;
        }

        if (formData.cliente_id) {
          payload.cliente_id = parseInt(formData.cliente_id);
        } else if (formData.cliente) {
          payload.cliente = formData.cliente;
        }

        const response = await api.post(endpoints.vuelos.create, payload);
        const flightNumber = response.data?.data?.flight_number;

        setSuccess('Vuelo creado exitosamente');
        setTimeout(() => {
          navigate(`/vuelos/${flightNumber}/gestionar`);
        }, 1000);
      }
    } catch (error) {
      const message = error.response?.data?.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el vuelo`;
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Si hay error y es modo edición sin datos cargados, mostrar mensaje de error
  if (isEditMode && error && !vueloOriginal) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          component={Link}
          to="/vuelos"
          variant="contained"
          startIcon={<BackIcon />}
        >
          Volver a la lista de vuelos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: isEditMode 
            ? 'linear-gradient(135deg, #E65100 0%, #BF360C 100%)'
            : 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isEditMode ? <EditIcon sx={{ fontSize: 48 }} /> : <FlightIcon sx={{ fontSize: 48 }} />}
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {isEditMode ? `Editar Vuelo: ${awb}` : 'Nuevo Vuelo'}
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>
                {isEditMode 
                  ? 'Modificar información del vuelo'
                  : 'Crear un nuevo vuelo para gestión de carga'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {/* Modo de creación - solo en modo nuevo */}
            {!isEditMode && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Modo de creación
                </Typography>
                <ToggleButtonGroup
                  value={modo}
                  exclusive
                  onChange={handleModoChange}
                  size="small"
                >
                  <ToggleButton value="ruta">
                    <RouteIcon sx={{ mr: 1 }} />
                    Usar Ruta Configurada
                  </ToggleButton>
                  <ToggleButton value="manual">
                    <FlightIcon sx={{ mr: 1 }} />
                    Destino Manual
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Info del destino actual en modo edición */}
            {isEditMode && vueloOriginal && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Destino actual:</strong> {vueloOriginal.Destino || vueloOriginal.destino}
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  El destino no puede ser modificado una vez creado el vuelo.
                </Typography>
              </Alert>
            )}

            {/* Información del Vuelo */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Información del Vuelo
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {!isEditMode && (
              <Alert severity="info" sx={{ mb: 3 }}>
                El número de vuelo se generará automáticamente al crear el vuelo.
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Fecha del Vuelo"
                  name="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Master AWB"
                  name="master"
                  value={formData.master}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="045-12345678"
                  helperText="Número de guía master (opcional)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Equipo / Aeronave</InputLabel>
                  <Select
                    name="equipo_id"
                    value={formData.equipo_id}
                    onChange={handleInputChange}
                    label="Equipo / Aeronave"
                  >
                    <MenuItem value="">Seleccione equipo</MenuItem>
                    {equipos.map((eq) => (
                      <MenuItem key={eq.id} value={eq.id}>
                        {eq.nombre} {eq.matricula && `(${eq.matricula})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Modo Ruta - solo en creación */}
              {!isEditMode && modo === 'ruta' && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Ruta</InputLabel>
                      <Select
                        name="ruta_id"
                        value={formData.ruta_id}
                        onChange={handleInputChange}
                        label="Ruta"
                      >
                        <MenuItem value="">Seleccione una ruta</MenuItem>
                        {rutas.map((ruta) => (
                          <MenuItem key={ruta.id} value={ruta.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={ruta.code} size="small" color="primary" />
                              <Typography>{ruta.ruta_visual || `${ruta.origen} → ${ruta.destino}`}</Typography>
                              {ruta.escala > 0 && (
                                <Chip label={`${ruta.escala} escala(s)`} size="small" variant="outlined" />
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Preview de Ruta */}
                  {selectedRuta && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'success.50',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'success.200',
                        }}
                      >
                        <Typography variant="subtitle2" color="success.dark" gutterBottom>
                          Ruta Seleccionada: {selectedRuta.code}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip label={selectedRuta.origen} size="small" color="success" />
                          {selectedRuta.escala >= 1 && selectedRuta.esc1 && (
                            <>
                              <ArrowIcon fontSize="small" />
                              <Chip label={selectedRuta.esc1} size="small" variant="outlined" />
                            </>
                          )}
                          {selectedRuta.escala >= 2 && selectedRuta.esc2 && (
                            <>
                              <ArrowIcon fontSize="small" />
                              <Chip label={selectedRuta.esc2} size="small" variant="outlined" />
                            </>
                          )}
                          {selectedRuta.escala >= 3 && selectedRuta.esc3 && (
                            <>
                              <ArrowIcon fontSize="small" />
                              <Chip label={selectedRuta.esc3} size="small" variant="outlined" />
                            </>
                          )}
                          <ArrowIcon fontSize="small" />
                          <Chip label={selectedRuta.destino} size="small" color="error" />
                        </Box>
                        {selectedRuta.capacidad > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Capacidad: {selectedRuta.capacidad.toLocaleString()} kg
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                </>
              )}

              {/* Modo Manual - solo en creación */}
              {!isEditMode && modo === 'manual' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Origen"
                      name="origen"
                      value={formData.origen}
                      onChange={handleInputChange}
                      fullWidth
                      disabled
                      helperText={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <InfoIcon sx={{ fontSize: 14 }} />
                          Automático según su base de operación
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Destino</InputLabel>
                      <Select
                        name="destino_id"
                        value={formData.destino_id}
                        onChange={handleInputChange}
                        label="Destino"
                      >
                        <MenuItem value="">Seleccione destino</MenuItem>
                        {estaciones.map((est) => (
                          <MenuItem key={est.id} value={est.id}>
                            {est.codigo || est.code} - {est.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Información Adicional */}
            <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }} gutterBottom>
              Información Adicional
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Handler"
                  name="handler"
                  value={formData.handler}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Nombre del handler"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Cliente (Facturación)</InputLabel>
                  <Select
                    name="cliente_id"
                    value={formData.cliente_id}
                    onChange={handleInputChange}
                    label="Cliente (Facturación)"
                  >
                    <MenuItem value="">Sin cliente</MenuItem>
                    {clientes.map((cli) => (
                      <MenuItem key={cli.id} value={cli.id}>
                        {cli.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color={isEditMode ? 'warning' : 'success'}
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                sx={{ flex: 1 }}
              >
                {saving 
                  ? (isEditMode ? 'Guardando...' : 'Creando...') 
                  : (isEditMode ? 'Guardar Cambios' : 'Crear Vuelo')}
              </Button>
              <Button
                component={Link}
                to="/vuelos"
                variant="contained"
                color="inherit"
                size="large"
                startIcon={<BackIcon />}
              >
                Cancelar
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VueloForm;
